import {
	Alert,
	MultiSelect,
	Select,
	Stack,
	TextInput,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import {
	conversationTypesQueryOptions,
	studentConversationsQueryOptions,
} from "../queries";
import type { ConversationLinkSelection } from "../emailConversationTarget";
import { ConversationMessageEditor } from './ConversationMessageEditor';
import { hasMessageContent } from '../messageDocument';
import { getConversationLinkLabel } from "../helpers";

export function ConversationLinkFields({
	studentNo,
	mode,
	value,
	onChange,
	disabled,
	defaultTitle,
}: {
	studentNo: string;
	mode: "existing" | "new";
	value: ConversationLinkSelection;
	onChange: (value: ConversationLinkSelection) => void;
	disabled: boolean;
	defaultTitle: string;
}) {
	const conversations = useQuery({
		...studentConversationsQueryOptions({ studentNo }),
		enabled: mode === "existing",
	});
	const types = useQuery({
		...conversationTypesQueryOptions({ studentNo }),
		enabled: mode === "new",
	});
	const permittedTypes = (types.data ?? []).filter(
		(type) => type.isPermitted && type.isActiveTimeWindow,
	);
	const typeOptions = [
		...new Map(
			permittedTypes.map((type) => [
				String(type.idTypeCfC),
				{ value: String(type.idTypeCfC), label: type.nameTypeCfC },
			]),
		).values(),
	];
	const classOptions = permittedTypes
		.filter(
			(type) =>
				String(type.idTypeCfC) === value.typeId && type.idStudentClass != null,
		)
		.map((type) => ({
			value: String(type.idStudentClass),
			label: type.classCode ?? String(type.idStudentClass),
		}));
	return (
		<Stack gap="sm">
			{mode === "existing" ? (
				<>
					{conversations.isError && (
						<Alert color="red">Conversations could not be loaded.</Alert>
					)}
					<MultiSelect
						label="Conversations"
						searchable
						required
						value={value.ids}
						onChange={(ids) => onChange({ ...value, ids })}
						placeholder={
							conversations.isPending
								? "Loading conversations…"
								: "Select one or more conversations"
						}
						disabled={
							disabled || conversations.isPending || conversations.isError
						}
						data={(conversations.data ?? [])
							.filter((conversation) => !conversation.isConversationClosed)
							.map((conversation) => ({
								value: String(conversation.idConversation),
								label: getConversationLinkLabel(conversation),
							}))}
					/>
				</>
			) : (
				<>
					{types.isError && (
						<Alert color="red">Conversation types could not be loaded.</Alert>
					)}
					<Select
						label="Type"
						required
						value={value.typeId}
						data={typeOptions}
						disabled={disabled || types.isPending || types.isError}
						onChange={(typeId) =>
							onChange({
								...value,
								typeId,
								classId: null,
								requiresClass: permittedTypes.some(
									(type) =>
										String(type.idTypeCfC) === typeId &&
										type.idStudentClass != null,
								),
							})
						}
					/>
					{!!classOptions.length && (
						<Select
							label="Class"
							required
							value={value.classId}
							data={classOptions}
							disabled={disabled}
							onChange={(classId) => onChange({ ...value, classId })}
						/>
					)}
					<TextInput
						label="Subject / title"
						required
						maxLength={255}
						value={value.title ?? defaultTitle}
						disabled={disabled}
						onChange={(event) =>
							onChange({ ...value, title: event.currentTarget.value })
						}
					/>
				</>
			)}
			<ConversationMessageEditor
				label="Message"
				value={value.message}
				format={value.messageFormat ?? 'plain'}
				disabled={disabled}
				onChange={message => onChange({ ...value, message, messageFormat: 'rich-text', messageHasContent: hasMessageContent(message) })}
			/>
		</Stack>
	);
}
